import { Monitor, Moon, Sun } from 'lucide-react'
import { useRef, type ComponentType, type KeyboardEvent } from 'react'
import { useTranslation } from 'react-i18next'

import { themes, type Theme } from '@/lib/theme/theme'
import { useTheme } from '@/lib/theme/useTheme'
import { cn } from '@/lib/utils'

const icons: Record<Theme, ComponentType<{ className?: string }>> = {
  light: Sun,
  dark: Moon,
  system: Monitor,
}

interface ThemeToggleProps {
  readonly className?: string
}

/**
 * Tema anahtarı — üç seçenekli bir radyo grubu (KARAR 2026-08-16, kullanıcı).
 *
 * **Üç seçenek, iki değil:** yalnız açık/koyu sunmak, işletim sistemi koyu moddayken uygulamayı
 * açık açar ve kullanıcıyı bunu her tarayıcıda elle düzeltmeye zorlar. "Sistem" varsayılan.
 *
 * **Döngüsel tek düğme değil:** tek düğme seçenekleri saklar ve kullanıcıya hangi durumda
 * olduğunu tıklayarak keşfettirir. Üçü de görünür durunca seçim bir tahmin olmaktan çıkıyor.
 *
 * Radix kurulu değil, o yüzden WAI-ARIA sözleşmesi elle kuruluyor — `Tabs`'taki kalıbın aynısı:
 * grup `radiogroup`, seçenekler `radio`, sıralı gezinmede yalnız seçili olan durur (`tabIndex`),
 * böylece Tab tuşu gruptan çıkar, seçenekler arasında dolaşmaz.
 */
export function ThemeToggle({ className }: ThemeToggleProps) {
  const { t } = useTranslation()
  const { theme, setTheme } = useTheme()
  const groupRef = useRef<HTMLDivElement>(null)

  const move = (event: KeyboardEvent<HTMLButtonElement>): void => {
    const next = nextTheme(event.key, themes.indexOf(theme))

    if (next === null) {
      return
    }

    event.preventDefault()
    setTheme(next)
    // Seçim değişince odak da taşınır: aksi hâlde ekran okuyucu kullanıcısı neyi seçtiğini
    // duymaz, odağı bıraktığı düğmede kalır.
    groupRef.current?.querySelector<HTMLButtonElement>(`[data-theme="${next}"]`)?.focus()
  }

  return (
    <div
      ref={groupRef}
      role="radiogroup"
      aria-label={t('theme.label')}
      className={cn('border-border flex items-center gap-0.5 rounded-md border p-0.5', className)}
    >
      {themes.map((option) => {
        const Icon = icons[option]
        const selected = option === theme

        return (
          <button
            key={option}
            type="button"
            role="radio"
            aria-checked={selected}
            // Etiket ikonun içinde değil burada: ikon dekoratif, adı taşıyan şey düğmenin
            // kendisi. Ekran okuyucu "Koyu, radyo düğmesi, seçili" diyebilsin diye.
            aria-label={t(`theme.options.${option}`)}
            title={t(`theme.options.${option}`)}
            data-theme={option}
            tabIndex={selected ? 0 : -1}
            onClick={() => setTheme(option)}
            onKeyDown={move}
            className={cn(
              'focus-visible:ring-ring rounded-sm p-1.5 focus-visible:ring-2 focus-visible:outline-none',
              selected
                ? 'bg-accent text-accent-foreground'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            <Icon className="size-4" />
          </button>
        )
      })}
    </div>
  )
}

/** Radyo grubunda ok tuşları **seçimi** taşır (WAI-ARIA); uçlarda sarmalanır. */
function nextTheme(key: string, current: number): Theme | null {
  const count = themes.length

  switch (key) {
    case 'ArrowRight':
    case 'ArrowDown':
      return themes[(current + 1) % count] ?? null
    case 'ArrowLeft':
    case 'ArrowUp':
      return themes[(current - 1 + count) % count] ?? null
    default:
      return null
  }
}
